import type { GTMContext, GTMEvent } from '@/types/gtm.js';
import type { Product } from '@/types/mode-list-products.js';

import useLocale from '@/composables/useLocale';

function formatStartDate(timestamp: number) {
  const { currentLocale } = useLocale();
  const dateLabel = hasDateLabel(timestamp);
  return dateLabel ?? new Intl.DateTimeFormat(currentLocale.value, { dateStyle: 'full', timeStyle: 'short' }).format(timestamp);
}

function formatLocalDate(timestamp: number) {
  const { currentLocale } = useLocale();
  const dateLabel = hasDateLabel(timestamp);
  return dateLabel ?? new Intl.DateTimeFormat(currentLocale.value, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }).format(timestamp);
}

function hasDateLabel(timestamp: number) {
  const { currentLocale } = useLocale();
  const activityDate = new Date(timestamp);
  const activityDay = activityDate.getDate();
  const activityMonth = activityDate.getMonth();
  const activityYear = activityDate.getFullYear();
  const today = new Date();
  const currentDay = today.getDate();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();

  const isToday = activityDay === currentDay && activityMonth === currentMonth && activityYear === currentYear;

  const tomorrow = new Date(today);
  tomorrow.setDate(currentDay + 1);
  const isTomorrow = activityDay === tomorrow.getDate() && activityMonth === tomorrow.getMonth() && activityYear === tomorrow.getFullYear();

  if (isToday) {
    return currentLocale.value === 'fr-FR' ? 'aujourd\'hui' : 'today';
  }
  else if (isTomorrow) {
    return currentLocale.value === 'fr-FR' ? 'dès demain' : 'tomorrow';
  }
  else {
    return undefined;
  }
}

function isEcoFriendly(product: Product): boolean {
  const extraProperties = product.properties.find(
    prop => prop.propertyName === 'productEnrichmentByDistributor',
  )?.value?.extra_properties;

  return extraProperties?.some(
    extraProp => extraProp.slug === 'eco-friendly' && extraProp.value === 'true',
  ) ?? false;
}

function convertUrlToResizer(imageURL: string, width: number = 1024, height: number = 780) {
  const encodedLink = btoa(imageURL);
  const convertionType = 'clip';
  const quality = 80;
  return `https://api.cloudly.space/resize/${convertionType}/${width}/${height}/${quality}/${encodedLink}/image.webp`;
}

function formatActivityDuration(minutes: number) {
  const { currentLocale } = useLocale();
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;

  const duration = {
    hours,
    minutes: remainingMinutes,
  };

  // NOTE: INVALID API IN MULTIPLE BROWSERS CAUSE TOO RECENT. CAUSE DISPLAY BUGS
  // if (typeof Intl.DurationFormat === 'function') {
  //   return new Intl.DurationFormat(currentLocale.value, { style: "long" }).format(duration);
  // }

  switch (currentLocale.value) {
    case 'fr-FR':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'heure')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minute')}` : ''}`;
    case 'es-ES':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'hora')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minuto')}` : ''}`;
    case 'de-DE':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'stunde')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minute')}` : ''}`;
    case 'en-US':
      return `${duration.hours ? `${duration.hours} ${pluralize(duration.hours, 'hour')} ` : ''}${duration.minutes ? `${duration.minutes} ${pluralize(duration.minutes, 'minute')}` : ''}`;
    default:
      console.error('Invalid locale definition');
      return '';
  }
}

function formatPrice(price: number, currency: 'EUR' | 'USD') {
  const locale = currency === 'EUR' ? 'fr-FR' : 'en-US';
  return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(price);
}

function pluralize(quantity: number, text: string) {
  return quantity > 1 ? `${text}s` : text;
}

declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    dataLayer?: object[];
  }
}

function pushEvent(gtmEvent: GTMEvent) {
  window.dataLayer = (window.dataLayer !== undefined) ? window.dataLayer : [];
  const layer = {
    event: `youbook_${gtmEvent.event}`,
    data: {
      ga4: {
        enable: true,
        label: gtmEvent.label,
        name: gtmEvent.name,
      },
    },
  };
  if (gtmEvent.data) {
    // @ts-expect-error youbook property is undefined in layer type
    layer.data.youbook = gtmEvent.data;
  }

  // Push to GTM
  if (layer.data.ga4 !== undefined) {
    window.dataLayer.push(layer);
  }
}

function pushContext(gtmContext: GTMContext) {
  window.dataLayer = (window.dataLayer !== undefined) ? window.dataLayer : [];
  const layer = {
    event: `youbook_${gtmContext.event}`,
    data: {
      youbook: gtmContext.data,
    },
  };

  // Push to GTM
  if (gtmContext.data !== undefined) {
    window.dataLayer.push(layer);
  }
}

export { convertUrlToResizer, formatActivityDuration, formatLocalDate, formatPrice, formatStartDate, isEcoFriendly, pushContext, pushEvent };
